import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './HomePage.css';

const divisions = [
  {
    id: 'infotech',
    label: 'IT Solutions',
    name: 'Ajera Infotech',
    tag: 'Technology',
    desc: 'Full-stack digital solutions, enterprise platforms & cloud transformation.',
    color: '#00cfff',
    link: 'https://infotech.ajeragroup.com',
    external: true,
    icon: '⬡',
  },
  {
    id: 'export',
    label: 'Agro Export',
    name: 'Ajera Export',
    tag: 'Agriculture',
    desc: 'Premium agro commodities — Moringa, spices & more — to 12+ countries.',
    color: '#4ade80',
    link: '/industries/export',
    external: false,
    icon: '⬡',
  },
  {
    id: 'construction',
    label: 'Construction',
    name: 'Ajera Builttech',
    tag: 'Infrastructure',
    desc: 'Residential, commercial & industrial construction built to last.',
    color: '#f59e0b',
    link: '/industries/construction',
    external: false,
    icon: '⬡',
  },
];

// const stats = [
//   { value: '3+', label: 'Industries' },
//   { value: '12+', label: 'Countries' },
//   { value: '50+', label: 'Team' },
//   { value: '2019', label: 'Founded' },
// ];

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const cursorRef = useRef<HTMLDivElement>(null);
  const [activeCard, setActiveCard] = useState<string | null>(null);

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!cursorRef.current) return;

      const hero = document.querySelector('.home-hero');
      if (!hero) return;

      const rect = hero.getBoundingClientRect();

      if (e.clientY < rect.top || e.clientY > rect.bottom) {
        cursorRef.current.style.webkitMaskImage =
          'radial-gradient(circle 0px at 0 0, transparent 100%)';
        (cursorRef.current.style as any).maskImage =
          'radial-gradient(circle 0px at 0 0, transparent 100%)';
        return;
      }

      const x = e.clientX;
      const y = e.clientY - rect.top;

      const gradient = `radial-gradient(circle 350px at ${x}px ${y}px,
        black 0%,
        rgba(0,0,0,0.4) 60%,
        transparent 80%
      )`;

      cursorRef.current.style.webkitMaskImage = gradient;
      (cursorRef.current.style as any).maskImage = gradient;
    };

    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, []);

  const handleDivisionClick = (div: typeof divisions[0]) => {
    if (div.external) {
      window.open(div.link, '_blank');
    } else {
      navigate(div.link);
    }
  };

  return (
    <div className="home-root">

      {/* HERO */}
      <section className="home-hero">
        <div className="cursor-spotlight" ref={cursorRef} />
        <div className="hero-grid-lines" />
        <div className="hero-orb hero-orb--1" />
        <div className="hero-orb hero-orb--2" />
        <div className="hero-orb hero-orb--3" />

        <div className="home-hero__content">
          <h1 className="hero-title">
            <span className="hero-title__line hero-title__line--accent">
              AJERA GROUP
            </span>
          </h1>

          <p className="hero-sub">
            Ajera Group is a multi-industry powerhouse — spanning IT Innovation,
            Agro Export & Construction — engineered for the future.
          </p>

          <div className="hero-actions">
            <button className="hero-btn hero-btn--primary" onClick={() => navigate('/who-we-are')}>
              Discover Ajera
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
            <button className="hero-btn hero-btn--ghost" onClick={() => navigate('/contact')}>
              Contact Us
            </button>
          </div>

        </div>

        <div className="hero-scroll-hint">
          <span />
          Scroll to explore
        </div>
      </section>

      {/* DIVISIONS */}
      <section className="divisions-section">
        <div className="divisions-inner">
          <div className="divisions-header">
            <span className="section-eyebrow">Our Divisions</span>
            <h2 className="divisions-title">Three Industries.<br />One Vision.</h2>
          </div>
          <div className="divisions-grid">
            {divisions.map((div) => (
              <div
                key={div.id}
                className={`division-card ${activeCard === div.id ? 'division-card--active' : ''}`}
                style={{ '--div-color': div.color } as React.CSSProperties}
                onMouseEnter={() => setActiveCard(div.id)}
                onMouseLeave={() => setActiveCard(null)}
                onClick={() => handleDivisionClick(div)}
              >
                <div className="division-card__glow" />
                <div className="division-card__top">
                  <span className="division-card__tag">{div.tag}</span>
                  {div.external && (
                    <svg className="division-card__ext" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"/>
                    </svg>
                  )}
                </div>
                <h3 className="division-card__name">{div.name}</h3>
                <p className="division-card__desc">{div.desc}</p>
                <div className="division-card__cta">
                  <span>Explore</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </div>
                <div className="division-card__num">
                  {String(divisions.indexOf(div) + 1).padStart(2, '0')}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY AJERA */}
      <section className="why-section">
        <div className="why-inner">
          <div className="why-left">
            <span className="section-eyebrow">Why Ajera</span>
            <h2 className="why-title">Built to solve<br />real problems</h2>
            <p className="why-desc">
              We don't just build products. We build ecosystems — connecting technology,
              agriculture, and infrastructure into one trusted enterprise.
            </p>
            <button className="hero-btn hero-btn--primary" onClick={() => navigate('/who-we-are')}>
              Our Story
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
          <div className="why-right">
            {[
              { icon: '◈', title: 'Multi-Industry', desc: 'Unique synergy across IT, Agro & Construction verticals' },
              { icon: '◉', title: 'Global Reach', desc: 'Operating in 12+ countries with trusted partnerships' },
              { icon: '◆', title: 'Technology-First', desc: 'Every division powered by modern digital infrastructure' },
              { icon: '◇', title: 'People-Centric', desc: 'Committed to sustainable growth for all stakeholders' },
            ].map(item => (
              <div className="why-card" key={item.title}>
                <span className="why-card__icon">{item.icon}</span>
                <div>
                  <h4 className="why-card__title">{item.title}</h4>
                  <p className="why-card__desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="cta-inner">
          <div className="cta-orb" />
          <span className="section-eyebrow">Let's Connect</span>
          <h2 className="cta-title">Ready to work together?</h2>
          <p className="cta-sub">
            Whether it's a project, partnership or career — we'd love to hear from you.
          </p>
          <div className="hero-actions">
            <button className="hero-btn hero-btn--primary" onClick={() => navigate('/contact')}>
              Get In Touch
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
            <button className="hero-btn hero-btn--ghost" onClick={() => navigate('/careers')}>
              View Careers
            </button>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;